/**
 * Episodes Popup — episodes-popup.js
 * ─────────────────────────────────────────────────────────────
 * Responsibilities:
 *   1. 3-D carousel slider (scale-based, centre card enlarged)
 *   2. Mouse drag + touch/swipe support on slider
 *   3. Click any card → open full-screen popup modal
 *   4. Modal: populate media (YouTube embed or static image)
 *   5. Modal: render episode description & watch-episode CTA
 *   6. Modal: render 2 product cards from server-side JSON
 *   7. Dynamic price refresh via /products/{handle}.js endpoint
 *   8. Modal prev / next navigation (keeps slider in sync)
 *   9. Keyboard navigation (Esc to close, arrow keys)
 *  10. Shopify theme-editor live reload support
 * ─────────────────────────────────────────────────────────────
 * Zero external dependencies — vanilla JS only.
 */

(function () {
  'use strict';

  /* ────────────────────────────────────────────────────────
     Utilities
  ──────────────────────────────────────────────────────── */

  function extractYouTubeId(url) {
    if (!url) return null;
    const re =
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
    const m = url.match(re);
    return m ? m[1] : null;
  }

  function buildEmbedUrl(videoId) {
    return (
      'https://www.youtube.com/embed/' +
      videoId +
      '?autoplay=1&rel=0&playsinline=1&enablejsapi=1'
    );
  }

  function formatPrice(val) {
    if (typeof val === 'string') return val;
    if (typeof val !== 'number') return '';
    const amount = (val / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return '\u20B9\u00A0' + amount;
  }

  function safeParseJSON(str) {
    if (!str || str === 'null') return null;
    try { return JSON.parse(str); } catch (e) { return null; }
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* ────────────────────────────────────────────────────────
     HTML builder — product card
  ──────────────────────────────────────────────────────── */

  function buildProductCard(prod, pos) {
    const card = document.createElement('a');
    card.className = 'ep-prod-card';
    card.setAttribute('aria-label', prod ? prod.title : 'Featured product ' + pos);

    if (!prod) {
      card.innerHTML =
        '<div class="ep-prod-img-placeholder">' +
        svgImage(36) +
        '</div>' +
        '<div class="ep-prod-info"><p class="ep-prod-empty">No product linked</p></div>';
      card.style.pointerEvents = 'none';
      return card;
    }

    card.href   = prod.url || '#';
    card.target = '_self';

    const hasCompare =
      prod.compare_at !== null &&
      prod.compare_at !== undefined &&
      prod.compare_at !== prod.price;

    card.innerHTML =
      '<div class="ep-prod-img-wrap">' +
      (prod.image
        ? '<img class="ep-prod-img" src="' + prod.image +
          '" alt="' + (prod.image_alt || '').replace(/"/g, '&quot;') +
          '" loading="lazy" width="400" height="400">'
        : '<div class="ep-prod-img-placeholder">' + svgImage(36) + '</div>') +
      '</div>' +
      '<div class="ep-prod-info">' +
      '<p class="ep-prod-title">' + (prod.title || '') + '</p>' +
      '<div class="ep-prod-prices" data-handle="' + (prod.handle || '') + '">' +
      (hasCompare
        ? '<span class="ep-prod-compare">' + formatPrice(prod.compare_at) + '</span>'
        : '') +
      '<span class="' + (hasCompare ? 'ep-prod-price ep-prod-price--sale' : 'ep-prod-price') + '">' +
      formatPrice(prod.price) +
      '</span>' +
      '</div>' +
      '</div>';

    return card;
  }

  function svgImage(size) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="1.5">' +
      '<rect x="3" y="3" width="18" height="18" rx="2"/>' +
      '<circle cx="8.5" cy="8.5" r="1.5"/>' +
      '<polyline points="21 15 16 10 5 21"/>' +
      '</svg>';
  }

  /* ────────────────────────────────────────────────────────
     EpisodesPopup class
  ──────────────────────────────────────────────────────── */

  class EpisodesPopup {
    constructor(sectionEl) {
      this._section   = sectionEl;
      this._sectionId = sectionEl.dataset.sectionId;

      /* Slider */
      this._viewport  = sectionEl.querySelector('.eps-track-viewport');
      this._track     = sectionEl.querySelector('#eps-track-' + this._sectionId);
      this._slides    = Array.from(sectionEl.querySelectorAll('.eps-slide'));
      this._prevBtn   = sectionEl.querySelector('.eps-arrow--prev');
      this._nextBtn   = sectionEl.querySelector('.eps-arrow--next');
      this._dots      = Array.from(sectionEl.querySelectorAll('.eps-dot'));

      /* Modal */
      this._modal        = document.getElementById('ep-modal-' + this._sectionId);
      this._backdrop     = this._modal && this._modal.querySelector('.ep-modal-backdrop');
      this._closeBtn     = this._modal && this._modal.querySelector('.ep-modal-close');
      this._modalPrev    = this._modal && this._modal.querySelector('.ep-modal-nav--prev');
      this._modalNext    = this._modal && this._modal.querySelector('.ep-modal-nav--next');
      this._iframe       = this._modal && this._modal.querySelector('#ep-modal-iframe-'    + this._sectionId);
      this._imgEl        = this._modal && this._modal.querySelector('#ep-modal-img-'       + this._sectionId);
      this._playBtn      = this._modal && this._modal.querySelector('#ep-modal-play-'      + this._sectionId);
      this._descEl       = this._modal && this._modal.querySelector('#ep-modal-desc-'      + this._sectionId);
      this._watchBtn     = this._modal && this._modal.querySelector('#ep-modal-btn-'       + this._sectionId);
      this._prodHeading  = this._modal && this._modal.querySelector('#ep-modal-prod-heading-' + this._sectionId);
      this._prodPanel    = this._modal && this._modal.querySelector('#ep-modal-products-'  + this._sectionId);

      /* State */
      this._current    = 0;
      this._total      = this._slides.length;
      this._open       = false;
      this._priceCache = {};

      /* Drag state (shared by mouse + touch) */
      this._dragStartX   = 0;
      this._dragLastX    = 0;
      this._dragStartTranslate = 0;
      this._isDragging   = false;
      this._dragMoved    = false;

      if (this._total === 0) return;
      this._init();
    }

    /* ── Init ───────────────────────────────────────────── */

    _init() {
      this._positionTrack(false);
      this._updateSlideClasses();
      this._bindSliderArrows();
      this._bindDots();
      this._bindSlideClick();
      this._bindDrag();         // mouse + touch
      this._bindModal();
      this._bindKeyboard();

      // Prefetch prices for slide 0
      if (this._slides[0]) this._prefetchPrices(this._slides[0]);
    }

    /* ── Track positioning ──────────────────────────────── */

    /**
     * Centre the active slide inside the viewport.
     * Uses the layout (pre-scale) offsetWidth of each slide
     * which is the correct value for translateX calculations.
     */
    _positionTrack(animate) {
      if (!this._track || !this._viewport) return;

      const vpWidth = this._viewport.offsetWidth;
      const gap     = this._getGap();

      // Sum widths of all slides before current index
      let offset = 0;
      for (let i = 0; i < this._current; i++) {
        offset += this._slides[i].offsetWidth + gap;
      }

      // Offset to place the active slide's centre at viewport centre
      const activeWidth   = this._slides[this._current].offsetWidth;
      const centreOffset  = (vpWidth - activeWidth) / 2;
      const translate     = centreOffset - offset;

      if (animate) {
        this._track.classList.remove('no-transition');
      } else {
        this._track.classList.add('no-transition');
        // Force reflow so the no-transition class takes effect immediately
        void this._track.offsetWidth;
      }
      this._track.style.transform = 'translateX(' + translate + 'px)';
      this._currentTranslate = translate;
    }

    _getGap() {
      if (!this._track) return 16;
      const raw = getComputedStyle(this._track).gap ||
                  getComputedStyle(this._track).columnGap || '16px';
      return parseFloat(raw) || 16;
    }

    /* ── Slide classes & dots ───────────────────────────── */

    _updateSlideClasses() {
      this._slides.forEach((slide, i) => {
        const active = i === this._current;
        slide.classList.toggle('eps-slide--active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this._dots.forEach((dot, i) => {
        const active = i === this._current;
        dot.classList.toggle('eps-dot--active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      if (this._prevBtn) {
        this._prevBtn.style.opacity = this._current === 0 ? '0.35' : '';
        this._prevBtn.toggleAttribute('disabled', this._current === 0);
      }
      if (this._nextBtn) {
        this._nextBtn.style.opacity = this._current === this._total - 1 ? '0.35' : '';
        this._nextBtn.toggleAttribute('disabled', this._current === this._total - 1);
      }
    }

    _goTo(index, animate = true) {
      index = Math.max(0, Math.min(index, this._total - 1));
      if (index === this._current && animate) return;
      this._current = index;
      this._positionTrack(animate);
      this._updateSlideClasses();
      this._prefetchPrices(this._slides[this._current]);
    }

    /* ── Arrow bindings ─────────────────────────────────── */

    _bindSliderArrows() {
      this._prevBtn && this._prevBtn.addEventListener('click', () => this._goTo(this._current - 1));
      this._nextBtn && this._nextBtn.addEventListener('click', () => this._goTo(this._current + 1));
    }

    /* ── Dot bindings ───────────────────────────────────── */

    _bindDots() {
      this._dots.forEach(dot => {
        dot.addEventListener('click', () => {
          this._goTo(parseInt(dot.dataset.dot, 10));
        });
      });
    }

    /* ── Slide click → open modal ───────────────────────── */

    _bindSlideClick() {
      this._slides.forEach(slide => {
        slide.addEventListener('click', () => {
          // Ignore click if this was actually a drag end
          if (this._dragMoved) return;
          const idx = parseInt(slide.dataset.slideIndex, 10);
          this._goTo(idx);
          this._openModal(idx);
        });

        slide.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const idx = parseInt(slide.dataset.slideIndex, 10);
            this._goTo(idx);
            this._openModal(idx);
          }
        });
      });
    }

    /* ── Drag (mouse + touch) ───────────────────────────── */

    _bindDrag() {
      if (!this._viewport) return;

      // ── TOUCH ──────────────────────────────────────────
      this._viewport.addEventListener('touchstart', e => {
        this._dragStartX         = e.touches[0].clientX;
        this._dragLastX          = this._dragStartX;
        this._dragStartTranslate = this._currentTranslate || 0;
        this._isDragging         = true;
        this._dragMoved          = false;
        this._track.classList.add('no-transition');
      }, { passive: true });

      this._viewport.addEventListener('touchmove', e => {
        if (!this._isDragging) return;
        const dx = e.touches[0].clientX - this._dragStartX;
        this._dragLastX = e.touches[0].clientX;
        if (Math.abs(dx) > 4) this._dragMoved = true;
        // Live-follow the finger
        this._track.style.transform =
          'translateX(' + (this._dragStartTranslate + dx) + 'px)';
      }, { passive: true });

      this._viewport.addEventListener('touchend', () => {
        if (!this._isDragging) return;
        this._isDragging = false;
        const dx = this._dragLastX - this._dragStartX;
        this._track.classList.remove('no-transition');

        if (Math.abs(dx) > 50) {
          this._goTo(dx < 0 ? this._current + 1 : this._current - 1);
        } else {
          // Snap back to current
          this._positionTrack(true);
        }
        // Reset dragMoved after a tiny delay so click handler can check it
        setTimeout(() => { this._dragMoved = false; }, 50);
      });

      // ── MOUSE ──────────────────────────────────────────
      this._viewport.addEventListener('mousedown', e => {
        if (e.button !== 0) return;
        e.preventDefault();
        this._dragStartX         = e.clientX;
        this._dragLastX          = e.clientX;
        this._dragStartTranslate = this._currentTranslate || 0;
        this._isDragging         = true;
        this._dragMoved          = false;
        this._viewport.classList.add('is-dragging');
        this._track.classList.add('no-transition');
      });

      // Attach mousemove & mouseup to document so drag works
      // even when cursor leaves the slider
      document.addEventListener('mousemove', e => {
        if (!this._isDragging) return;
        const dx = e.clientX - this._dragStartX;
        this._dragLastX = e.clientX;
        if (Math.abs(dx) > 4) this._dragMoved = true;
        this._track.style.transform =
          'translateX(' + (this._dragStartTranslate + dx) + 'px)';
      });

      document.addEventListener('mouseup', () => {
        if (!this._isDragging) return;
        this._isDragging = false;
        this._viewport.classList.remove('is-dragging');
        this._track.classList.remove('no-transition');

        const dx = this._dragLastX - this._dragStartX;
        if (Math.abs(dx) > 50) {
          this._goTo(dx < 0 ? this._current + 1 : this._current - 1);
        } else {
          this._positionTrack(true);
        }
        setTimeout(() => { this._dragMoved = false; }, 50);
      });
    }

    /* ── Modal: open ────────────────────────────────────── */

    _openModal(index) {
      if (!this._modal) return;
      this._current = Math.max(0, Math.min(index, this._total - 1));
      this._populateModal(this._current);
      this._modal.removeAttribute('hidden');
      this._modal.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden';
      this._open = true;
      this._updateModalNav();
      if (this._closeBtn) {
        requestAnimationFrame(() => this._closeBtn.focus());
      }
    }

    /* ── Modal: close ───────────────────────────────────── */

    _closeModal() {
      if (!this._modal) return;
      if (this._iframe) this._iframe.src = '';
      this._modal.setAttribute('hidden', '');
      this._modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this._open = false;
      const slide = this._slides[this._current];
      if (slide) slide.focus();
    }

    /* ── Modal: populate ────────────────────────────────── */

    _populateModal(index) {
      const slide = this._slides[index];
      if (!slide || !this._modal) return;

      const youtubeUrl = slide.dataset.youtube || '';
      const desc       = slide.dataset.desc    || '';
      const btnLabel   = slide.dataset.btn     || 'Watch Full Episode';
      const title      = slide.dataset.title   || '';
      const prod1Data  = safeParseJSON(slide.dataset.prod1);
      const prod2Data  = safeParseJSON(slide.dataset.prod2);

      /* Media */
      const videoId = extractYouTubeId(youtubeUrl);
      if (videoId) {
        if (this._iframe) {
          this._iframe.src = buildEmbedUrl(videoId);
          this._iframe.removeAttribute('hidden');
        }
        if (this._imgEl)   this._imgEl.setAttribute('hidden', '');
        if (this._playBtn) this._playBtn.setAttribute('hidden', '');
      } else {
        if (this._iframe) {
          this._iframe.src = '';
          this._iframe.setAttribute('hidden', '');
        }
        const cardImg = slide.querySelector('.eps-card-img');
        if (this._imgEl) {
          this._imgEl.src = cardImg ? cardImg.src : '';
          this._imgEl.alt = title;
          if (cardImg) {
            this._imgEl.removeAttribute('hidden');
          } else {
            this._imgEl.setAttribute('hidden', '');
          }
        }
        if (this._playBtn) this._playBtn.setAttribute('hidden', '');
      }

      /* Description */
      if (this._descEl) this._descEl.textContent = desc;

      /* Watch button */
      if (this._watchBtn) {
        if (youtubeUrl) {
          this._watchBtn.textContent = btnLabel + ' →';
          this._watchBtn.href = youtubeUrl;
          this._watchBtn.removeAttribute('hidden');
        } else {
          this._watchBtn.setAttribute('hidden', '');
        }
      }

      /* Products */
      this._renderProducts(prod1Data, prod2Data);
    }

    /* ── Products render + price refresh ───────────────── */

    _renderProducts(prod1, prod2) {
      if (!this._prodPanel) return;
      this._prodPanel.innerHTML = '';

      const hasProd = prod1 || prod2;

      // Show/hide the "Products Featured" heading
      if (this._prodHeading) {
        if (hasProd) {
          this._prodHeading.removeAttribute('hidden');
        } else {
          this._prodHeading.setAttribute('hidden', '');
        }
      }

      if (!hasProd) {
        const empty = document.createElement('p');
        empty.className = 'ep-prod-empty';
        empty.textContent = 'No products featured in this episode.';
        this._prodPanel.appendChild(empty);
        return;
      }

      const card1 = buildProductCard(prod1, '1');
      const card2 = buildProductCard(prod2, '2');
      this._prodPanel.appendChild(card1);
      this._prodPanel.appendChild(card2);

      this._refreshPrices(prod1, card1);
      this._refreshPrices(prod2, card2);
    }

    _refreshPrices(prodData, cardEl) {
      if (!prodData || !prodData.handle) return;
      const handle   = prodData.handle;
      const priceRow = cardEl.querySelector('.ep-prod-prices');
      if (!priceRow) return;

      if (this._priceCache[handle]) {
        this._applyPriceData(priceRow, this._priceCache[handle]);
        return;
      }

      fetchProduct(handle).then(data => {
        if (!data) return;
        const cached = { price: data.price, compare_at: data.compare_at_price || null };
        this._priceCache[handle] = cached;
        this._applyPriceData(priceRow, cached);
      });
    }

    _applyPriceData(priceRow, data) {
      const hasCompare =
        data.compare_at !== null &&
        data.compare_at !== undefined &&
        data.compare_at > data.price;

      priceRow.innerHTML =
        (hasCompare ? '<span class="ep-prod-compare">' + formatPrice(data.compare_at) + '</span>' : '') +
        '<span class="' + (hasCompare ? 'ep-prod-price ep-prod-price--sale' : 'ep-prod-price') + '">' +
        formatPrice(data.price) +
        '</span>';
    }

    _prefetchPrices(slide) {
      [slide.dataset.prod1, slide.dataset.prod2].forEach(raw => {
        const prod = safeParseJSON(raw);
        if (!prod || !prod.handle || this._priceCache[prod.handle]) return;
        fetchProduct(prod.handle).then(data => {
          if (!data) return;
          this._priceCache[prod.handle] = {
            price:      data.price,
            compare_at: data.compare_at_price || null,
          };
        });
      });
    }

    /* ── Modal navigation ───────────────────────────────── */

    _updateModalNav() {
      if (this._modalPrev) this._modalPrev.toggleAttribute('hidden', this._current === 0);
      if (this._modalNext) this._modalNext.toggleAttribute('hidden', this._current === this._total - 1);
    }

    _modalNavigate(delta) {
      const next = this._current + delta;
      if (next < 0 || next >= this._total) return;
      this._current = next;
      this._goTo(this._current);
      this._populateModal(this._current);
      this._updateModalNav();
    }

    /* ── Modal bindings ─────────────────────────────────── */

    _bindModal() {
      if (!this._modal) return;
      this._backdrop && this._backdrop.addEventListener('click',  () => this._closeModal());
      this._closeBtn && this._closeBtn.addEventListener('click',  () => this._closeModal());
      this._modalPrev && this._modalPrev.addEventListener('click', () => this._modalNavigate(-1));
      this._modalNext && this._modalNext.addEventListener('click', () => this._modalNavigate(1));
    }

    /* ── Keyboard ───────────────────────────────────────── */

    _bindKeyboard() {
      document.addEventListener('keydown', e => {
        if (this._open) {
          if (e.key === 'Escape')      { e.preventDefault(); this._closeModal(); }
          else if (e.key === 'ArrowLeft')  { e.preventDefault(); this._modalNavigate(-1); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); this._modalNavigate(1); }
        } else {
          if (this._section.contains(document.activeElement)) {
            if (e.key === 'ArrowLeft')  { e.preventDefault(); this._goTo(this._current - 1); }
            else if (e.key === 'ArrowRight') { e.preventDefault(); this._goTo(this._current + 1); }
          }
        }
      });
    }
  }

  /* ────────────────────────────────────────────────────────
     Bootstrap
  ──────────────────────────────────────────────────────── */

  function init() {
    document.querySelectorAll('.eps-section[data-section-id]').forEach(el => {
      if (!el._epsPopupInstance) {
        el._epsPopupInstance = new EpisodesPopup(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Shopify theme editor events */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', e => {
      const section = e.target.querySelector('.eps-section[data-section-id]');
      if (section) section._epsPopupInstance = new EpisodesPopup(section);
    });
    document.addEventListener('shopify:section:unload', e => {
      const section = e.target.querySelector('.eps-section[data-section-id]');
      if (section) section._epsPopupInstance = null;
    });
    document.addEventListener('shopify:block:select', e => {
      const slide = e.target;
      if (!slide.classList.contains('eps-slide')) return;
      const section = slide.closest('.eps-section[data-section-id]');
      if (section && section._epsPopupInstance) {
        section._epsPopupInstance._goTo(parseInt(slide.dataset.slideIndex, 10));
      }
    });
  }
})();
